// Anime Source Context - AniList wrapper
// Provides a unified API wrapper around AniList context

import React, { createContext, useContext, useMemo } from 'react';
import { useAniList } from './AniListContext';
import { useAdultContent } from './AdultContentContext';

const AnimeSourceContext = createContext();

export const useAnimeSource = () => {
    const context = useContext(AnimeSourceContext);
    if (!context) {
        throw new Error('useAnimeSource must be used within AnimeSourceProvider');
    }
    return context;
};

export const AnimeSourceProvider = ({ children }) => {
    const anilist = useAniList();
    const { hideAdultContent } = useAdultContent();

    // Filter out groups containing adult content when the setting is enabled
    const filteredAnimeGroups = useMemo(() => {
        const groups = anilist.animeGroups || [];
        if (!hideAdultContent) return groups;
        return groups.filter(group => {
            const entries = group.anime || [];
            return !entries.some(entry => entry.media?.isAdult);
        });
    }, [anilist.animeGroups, hideAdultContent]);

    const value = {
        // Source info
        activeSource: 'anilist',

        // Connection status
        isConnected: anilist.isConnected,

        // All data from AniList
        username: anilist.username,
        userData: anilist.userData,
        isLoading: anilist.isLoading,
        isRefreshing: anilist.isRefreshing,
        error: anilist.error,
        lastUpdated: anilist.lastUpdated,

        // Groups & relations
        animeGroups: filteredAnimeGroups,
        pendingAnime: anilist.pendingAnime || [],
        relationsData: anilist.relationsData || {},
        fetchProgress: anilist.fetchProgress || { progress: 0, status: '' },
        isProcessingGroups: anilist.isProcessingGroups || false,

        // Actions
        refreshData: anilist.refreshData || (() => { }),
        updateUserDataDirect: anilist.updateUserDataDirect || (() => { }),
        fetchAndBuildGroups: anilist.fetchAndBuildGroups || (() => { }),
        addPendingAnimeToList: anilist.addPendingAnimeToList || (() => { }),
        clearGroupsCache: anilist.clearGroupsCache || (() => { }),

        // Token
        token: anilist.token,

        // Access to AniList context
        anilist
    };

    return (
        <AnimeSourceContext.Provider value={value}>
            {children}
        </AnimeSourceContext.Provider>
    );
};
